import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import Fuse from "fuse.js";
import { Upload, Info, Loader2, Table, FileText, XCircle, CheckCircle } from 'lucide-react'; // Added icons
import Button from '../components/ui/Button'; // Our Button component
import Input from '../components/ui/Input'; // Our Input component
import FastAPIClient from "../services/apiClient";
import { API_BASE_PATH, parseNumber } from "../apiHelper";
import { useToast } from "../hooks/useToast"; // Our toast hook
import { ro } from "date-fns/locale";
import Modal from "../components/ui/Modal";

const client = new FastAPIClient();

const cleanupValue = (value) => {
    // Check if the value is an empty string or a string made only of whitespace
    if (typeof value === 'string' && value.trim() === '') {
        return null;
    }
    // Return the value as is if it's already null, a number, a valid string, etc.
    return value;
};


const ImportData = () => {
    const location = useLocation();
    const navigate = useNavigate()
    const { showToast } = useToast(); // Use our toast hook

    const [tag, setTag] = useState("");
    const [expectedSchema, setExpectedSchema] = useState([]);
    const [user, setUser] = useState(null);
    const [endpoint, setEndpoint] = useState("");
    const [loading, setLoading] = useState(false);

    // ColumnMapper states, now integrated
    const [fileName, setFileName] = useState(null);
    const [columns, setColumns] = useState([]); // Columns from the uploaded file
    const [rawData, setRawData] = useState([]); // Raw data from the uploaded file
    const [mapping, setMapping] = useState({}); // User's column mapping
    const [currentPage, setCurrentPage] = useState(1);
    const [failedRows, setFailedRows] = useState([]);

    const [specialValue, setSpecialValue] = useState(null);
    const [target, setTarget] = useState(null);

    const failedRowsRef = useRef(null);
    const rowsPerPage = 5;

    const [isGuideOpen, setIsGuideOpen] = useState(false);
    const [guideData, setGuideData] = useState(null);

    // Effect to load user from local storage
    useEffect(() => {
        const lcUser = localStorage.getItem("user");
        if (!lcUser) {
            // In a real app, you might redirect to login or show a login prompt
            console.warn("User not found in local storage. Please log in.");
            showToast("Please log in to import data.", "info");
            navigate('/login')
        } else {
            setUser(JSON.parse(lcUser));
        }
    }, [showToast]);

    useEffect(() => {
        // Only fetch if we are on the batchcategories screen
        if (target === "batchcategories") {
            const fetchGuide = async () => {
                try {
                    // Assuming your FastAPI client has a get method
                    const data = await client.fetchData(`${API_BASE_PATH}/onboarding/csv-guide-categories`);
                    setGuideData(data);
                } catch (err) {
                    const errorMessage = err.response?.data?.detail || "Error fetching guide instructions";
                    showToast(errorMessage, "error")
                }
            };
            fetchGuide();
        } else if (target == "batchrawmaterials") {
            const fetchGuide = async () => {
                try {
                    const data = await client.fetchData(`${API_BASE_PATH}/onboarding/csv-guide-catalog-entries`);
                    setGuideData(data);
                } catch (err) {
                    const errorMessage = err.response?.data?.detail || "Error fetching guide instructions";
                    showToast(errorMessage, "error");
                }
            };
            fetchGuide();
        } else if (target == "batchproducts") {
            const fetchGuide = async () => {
                try {
                    const data = await client.fetchData(`${API_BASE_PATH}/onboarding/csv-guide-catalog-entries`);
                    setGuideData(data);
                } catch (err) {
                    const errorMessage = err.response?.data?.detail || "Error fetching guide instructions";
                    showToast(errorMessage, "error")
                }
            };
            fetchGuide();
        } else if (target == "customer-groups") {
            const fetchGuide = async () => {
                try {
                    const data = await client.fetchData(`${API_BASE_PATH}/onboarding/csv-guide-customer-groups`);
                    setGuideData(data)
                } catch (err) {
                    const errorMessage = err.response?.data?.detail || "Error fetching guide instructions";
                    showToast(errorMessage, "error");
                }
            };
            fetchGuide();
        } else if (target == "customers") {
            const fetchGuide = async () => {
                try {
                    const data = await client.fetchData(`${API_BASE_PATH}/onboarding/csv-guide-customer-list`);
                    setGuideData(data);
                } catch (err) {
                    const errorMessage = err.response?.data?.detail || "Error fetching guide instructions";
                    showToast(errorMessage, "error");
                }
            };
            fetchGuide();
        } else if (target == "grouped-customers") {
            const fetchGuide = async () => {
                try {
                    const data = await client.fetchData(`${API_BASE_PATH}/onboarding/csv-guide-grouped-customer-list`);
                    setGuideData(data);
                } catch (err) {
                    const errorMessage = err.response?.data?.detail || "Error fetching guide instructions."
                    showToast(errorMessage, "error");
                }
            };
            fetchGuide();
        }
    }, [target]);

    // Effect to determine schema and endpoint based on route state
    useEffect(() => {
        if (location.state) {
            const { target, ...others } = location.state;
            setTarget(target)
            switch (target) {
                case "accounts":
                    setTag("Accounts");
                    setEndpoint("/accounting/import-accounts");
                    setExpectedSchema(['name', 'account_type', 'account_code', 'detail_type', 'description', 'currency', 'balance', 'parent_account', 'branch']);
                    break;
                case "accounttypes":
                    setTag("Account Type");
                    setEndpoint("/accounting/import-account-types");
                    setExpectedSchema(['label', "parent", "description", "code_prefix"]);
                    break;
                case "bankaccounts":
                    setTag("Bank Accounts");
                    setEndpoint("/accounting/upload-bank-accounts");
                    setExpectedSchema(['name','account_type', 'detail_type', 'branch', 'description', 'balance', 'currency']);
                    break;
                case "transactionmaps":
                    setTag("Transaction Mapping");
                    setEndpoint("/accounting/import-transaction-maps")
                    setExpectedSchema(["mapping_id", "transaction_type", "debit_account_name", "credit_account_name", "description", "is_active", "canSave"])
                    break;
                case "batchemployees":
                    setTag("Batch Import Non-User Employees");
                    setEndpoint("/auth/non-user-import");
                    setExpectedSchema(["name", "email", "phone"]);
                    break;
                case "batchrawmaterials":
                    setTag("Batch Import Raw Materials");
                    setEndpoint("/product/batch-import-items");
                    setExpectedSchema(
                        ["productname", "catalog", "initialquantity", "store", "currency", "listprice",
                            "costprice", "category", "unitofmeasurement", "expirydate", "partnumber", "forsale"]);
                    break;
                case "batchproducts":
                    setTag("Batch Import Catalog Entries");
                    setEndpoint("/product/batch-import-items");
                    // setExpectedSchema(["partnumber", "productname", "catalog", "category", "initialquantity", "store", "currency", "listprice", "costprice", "expirydate"]);
                    setExpectedSchema(
                        ["productname", "catalog", "initialquantity", "store", "currency", "listprice",
                            "costprice", "category", "unitofmeasurement", "expirydate", "partnumber","forsale"]);
                    break;
                case "customers":
                    setTag("Customer List");
                    setEndpoint("/auth/import-customers");
                    setExpectedSchema(["name", "address", "contact", "phone", "currency", "openingbalance", "branch", "email"]);
                    break;
                case "grouped-customers":
                    setTag("Grouped Customers");
                    setEndpoint("/users/import-grouped-customers");
                    setExpectedSchema(["contact", "email", "phone", "customername", "group", "branch", "balance"]);
                    // setExpectedSchema(["ContactFullName", "Email", "Phone", "CustomerName", "GroupName", "Branch"])
                    break;
                case "batchvendors":
                    setTag("Import your vendor list");
                    setEndpoint("/vendor/import-list");
                    setExpectedSchema(["name", "address", "contact", "phone", "currency", "openingbalance"]);
                    break;
                case "batchcategories":
                    setTag("Batch Import Product Categories")
                    setEndpoint("/product/batch-import-categories")
                    setExpectedSchema(["CatalogName", "ParentCategory", "ChildCategory"])
                    break;
                case "customer-group-members":
                    setSpecialValue(others.groupId);
                    setTag(`Group Members: ${others.groupName}`);
                    setEndpoint("/users/import-group-members");
                    setExpectedSchema(["ContactFullName", "Email", "Phone", "CustomerName", "GroupName", "Branch"])
                    // setExpectedSchema(["parents_first_name", "parents_last_name", "parents_email", "studentnumber", "students_first_name", "students_last_name", "group_name", "branch"]);
                    // setExpectedSchema(['first_name','last_name','email','id_number','group_name']);
                    break;
                case "students":
                    setTag("Students");
                    setEndpoint("/auth/student-invite");
                    setExpectedSchema(['first_name', 'last_name', 'email', 'admission_number', 'class_room', 'admitted_on']);
                    break;
                case "customer-groups":
                    setTag("Customer Groups");
                    setEndpoint("/users/import-groups");
                    setExpectedSchema(["name", "description", "branch"])
                    break;
                case "classrooms":
                    setTag("Classrooms");
                    setEndpoint("/school/import-classrooms");
                    setExpectedSchema(["name", "description"]);
                    break;
                case "curricula":
                    setTag("Curricula");
                    setEndpoint("/school/import-curricula");
                    setExpectedSchema(["name", "academic_year"]);
                    break;
                case "subjects":
                    setTag("Subjects");
                    setEndpoint("/school/import-subjects");
                    setExpectedSchema(["curriculum_name", "subject_name", "subject_code"]);
                    break;
                default:
                    console.log("No specific target state passed in location or target not recognized.");
                    setTag("Generic Data");
                    setEndpoint("/generic/import"); // Fallback or error
                    setExpectedSchema([]);
            }
            // Clear previous file data when target changes
            setFileName(null);
            setColumns([]);
            setRawData([]);
            setMapping({});
            setCurrentPage(1);
            setFailedRows([]);
        } else {
            console.log("No state passed in location.");
            setTag("Generic Data");
            setEndpoint("/generic/import"); // Fallback
            setExpectedSchema([]);
        }
    }, [location.state]);

    // Function to generate initial column mapping suggestions
    const generateInitialMapping = useCallback((fields) => {
        const fuse = new Fuse(expectedSchema, { includeScore: true, threshold: 0.4 }); // Lower threshold for stricter matches
        const suggestions = {};
        fields.forEach((field) => {
            const result = fuse.search(field)[0];
            suggestions[field] = result ? result.item : 'ignore'; // Map if a good match, else ignore
        });
        setMapping(suggestions);
    }, [expectedSchema]);

    // Handlers for file parsing
    const parseCSV = useCallback((file) => {
        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            worker: true,
            complete: (results) => {
                setColumns(results.meta.fields || []);
                setRawData(results.data || []);
                generateInitialMapping(results.meta.fields || []);
            },
            error: (err) => {
                showToast(`Error parsing CSV: ${err.message}`, 'error');
            }
        });
    }, [generateInitialMapping, showToast]);

    const parseExcel = useCallback((file) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const workbook = XLSX.read(e.target.result, { type: 'binary' });
                const sheet = workbook.Sheets[workbook.SheetNames[0]];
                const jsonData = XLSX.utils.sheet_to_json(sheet, { defval: '' });
                const fields = Object.keys(jsonData.length > 0 ? jsonData[0] : {});
                setColumns(fields);
                setRawData(jsonData);
                generateInitialMapping(fields);
            } catch (err) {
                showToast(`Error parsing Excel: ${err.message}`, 'error');
            }
        };
        reader.onerror = (err) => {
            showToast(`Error reading file: ${err.message}`, 'error');
        };
        reader.readAsBinaryString(file);
    }, [generateInitialMapping, showToast]);

    const handleFile = useCallback((e) => {
        const file = e.target.files[0];
        if (!file) return;

        setFileName(file.name);
        setFailedRows([]); // Clear previous failed rows on new file upload

        if (file.name.endsWith('.csv')) {
            parseCSV(file);
        } else if (file.name.endsWith('.xls') || file.name.endsWith('.xlsx')) {
            parseExcel(file);
        } else {
            showToast("Unsupported file type. Please upload a CSV or Excel file.", 'error');
            setFileName(null);
            setColumns([]);
            setRawData([]);
            setMapping({});
        }
    }, [parseCSV, parseExcel, showToast]);

    const handleMappingChange = useCallback((uploadedCol, targetCol) => {
        setMapping((prev) => ({ ...prev, [uploadedCol]: targetCol }));
    }, []);

    // Memoized transformed data
    const transformedData = useMemo(() => {
        return rawData.map((row) => {
            const newRow = {};
            Object.entries(mapping).forEach(([uploadedCol, mappedCol]) => {
                if (mappedCol !== 'ignore' && row[uploadedCol] !== undefined) {
                    // Specific transformation for 'Students' 'admitted_on' field if needed
                    if (tag === "Students" && mappedCol === "admitted_on") {
                        // Attempt to parse date from various formats
                        let dateValue = row[uploadedCol];
                        if (typeof dateValue === 'number') { // Excel dates are numbers
                            dateValue = XLSX.SSF.format('yyyy-mm-dd', dateValue);
                        }
                        // Add more date parsing logic if needed for other string formats
                        newRow[mappedCol] = dateValue;
                    } else if (tag === "Students" && mappedCol === "id_number") {
                        // Map admission_number to id_number for students
                        newRow['id_number'] = row[uploadedCol];
                    }
                    else {
                        newRow[mappedCol] = row[uploadedCol];
                    }
                }
            });
            return newRow;
        });
    }, [rawData, mapping, tag]);


    const handleUploadComplete = async (transformedDataToUpload) => {
        if (!user || !user.users_id) {
            console.warn("Upload attempted before user is available.");
            showToast("User not logged in. Cannot upload data.", "error");
            return;
        }
        if (!endpoint) {
            console.warn("No API endpoint defined for this data type.");
            showToast("Cannot upload: No API endpoint defined for this data type.", "error");
            return;
        }

        let dataToSend = transformedDataToUpload;

        // Specific transformations for the payload based on tag
        if (tag === "Students") {
            dataToSend = transformedDataToUpload.map(row => ({
                first_name: row.first_name,
                last_name: row.last_name,
                email: row.email,
                id_number: row.admission_number || row.id_number, // Use admission_number from source, map to id_number
                class_room: row.class_room,
                admission_date: row.admitted_on // Assuming 'admitted_on' is correctly formatted
            }));
        } else if (tag == "Customer List") {
            dataToSend = transformedData.map(row => {

                // Apply the cleanup function within your map
                // name, address, contact, phone, currency, openingbalance, branch, email
                return {
                    name: cleanupValue(row.name),
                    address: cleanupValue(row.address),
                    contact: cleanupValue(row.contact),
                    phone: cleanupValue(row.phone),
                    // Numeric fields like openingbalance MUST be null or a number, 
                    // so converting "" to null is essential here.
                    currency: cleanupValue(row.currency),
                    openingbalance: cleanupValue(row.openingbalance),
                    branch: cleanupValue(row.branch),
                    email: cleanupValue(row.email)
                };

            })
        } else if(tag == "Grouped Customers") {
            dataToSend = transformedData.map(row => {
                // setExpectedSchema(["ContactFullName", "Email", "Phone", "CustomerName", "GroupName", "Branch"])
                return {
                    contact: cleanupValue(row.contact),
                    email: cleanupValue(row.email),
                    phone: cleanupValue(row.phone),
                    customername: cleanupValue(row.customername),
                    group: cleanupValue(row.group),
                    branch: cleanupValue(row.branch),
                    balance: cleanupValue(row.balance),
                };
            })
        } else if (tag == "Import your vendor list") {
            dataToSend = transformedData.map(row => {
                return {
                    name: cleanupValue(row.name),
                    address: cleanupValue(row.address),
                    contact: cleanupValue(row.contact),
                    phone: cleanupValue(row.phone),
                    currency: cleanupValue(row.currency),
                    openingbalance: cleanupValue(row.openingbalance),
                }
            })
        } else if (tag == "Batch Import Catalog Entries" || tag == "Batch Import Raw Materials") {
            dataToSend = transformedData.map(row => {
                const rawExpiryDate = row.expirydate;

                let formattedExpiryDate = null;
                if (rawExpiryDate) {
                    const dateObj = new Date(rawExpiryDate.replace(/(\d+)\/(\d+)\/(\d+)/, '$3-$1-$2'));
                    if (!isNaN(dateObj)) {
                        formattedExpiryDate = dateObj.toISOString().split('T')[0];
                    } else {
                        console.error(`Ivalid date  found for expirydate: ${rawExpiryDate}`)
                        formattedExpiryDate = null;
                    }
                }
                if (tag == "Batch Import Catalog Entries") {
                    return {
                        partnumber: row.partnumber,
                        productname: row.productname,
                        catalog_id: row.catalog,
                        category_id: row.category,
                        initialquantity: parseNumber(row.initialquantity),
                        store_id: row.store,
                        currency: row.currency,
                        listprice: parseNumber(row.listprice),
                        costprice: parseNumber(row.costprice),
                        member_id: parseInt(user.users_id),
                        expirydate: formattedExpiryDate,
                        quantitymeasure: row.unitofmeasurement,
                        buyable: parseNumber(row.forsale),
                        itemtype: "FINISHED_GOODS",
                    };
                } else if (tag === "Batch Import Raw Materials") {
                    return {
                        partnumber: row.partnumber,
                        productname: row.productname,
                        catalog_id: row.catalog,
                        category_id: row.category,
                        initialquantity: parseNumber(row.initialquantity),
                        store_id: row.store,
                        currency: row.currency,
                        listprice: parseNumber(row.listprice),
                        costprice: parseNumber(row.costprice),
                        member_id: parseInt(user.users_id),
                        expirydate: formattedExpiryDate,
                        quantitymeasure: row.unitofmeasurement,
                        buyable: parseNumber(row.forsale),
                        itemtype: "RAW_MATERIALS",
                    };
                }
            });
        }

        if (target === "customer-group-members") {
            dataToSend = transformedData.map(row => ({ ...row, group_id: specialValue }))
        }
        // Add more tag-specific transformations here if needed for backend payload structure

        const payload = { users_id: user.users_id, rows: dataToSend };
        console.log("🚀 Transformed Data Ready for Upload:", payload);

        setLoading(true);
        try {
            const response = await client.postData(`${API_BASE_PATH}${endpoint}`, payload);
            // console.log("Upload Response:", response);
            if (response?.failed_rows && response.failed_rows.length > 0) {
                setFailedRows(response.failed_rows);
                showToast(`Upload complete with ${response.failed_rows.length} failed rows.`, 'warning');
            } else {
                setFailedRows([]);
                showToast(response?.msg || "Data uploaded successfully!", 'success');
            }
            // Clear data after successful upload (or partial success)
            setFileName(null);
            setColumns([]);
            setRawData([]);
            setMapping({});
            setCurrentPage(1);
            return response;
        } catch (err) {
            console.error("Error during upload:", err);
            const errorMessage = err.response?.data?.detail || `Error uploading data: ${err.message || err}`;
            showToast(errorMessage, 'error');
            // If there's a backend error with failed_rows, set them
            if (err.response?.data?.failed_rows) {
                setFailedRows(err.response.data.failed_rows);
            }
            throw err; // Re-throw to allow component to handle finally block
        } finally {
            setLoading(false);
        }
    };

    const handleUpload = async () => {
        if (transformedData.length === 0) {
            showToast("No data to upload. Please upload a file and map columns.", 'info');
            return;
        }
        await handleUploadComplete(transformedData);
    };

    const pageData = useMemo(() => {
        return transformedData.slice(
            (currentPage - 1) * rowsPerPage,
            currentPage * rowsPerPage
        );
    }, [transformedData, currentPage, rowsPerPage]);

    const isValidString = (str) => { return str !== null && str.trim() !== "" };
    const title = `🧩 Column Mapper${isValidString(tag) ? ` - ${tag}` : ""}`;

    useEffect(() => {
        if (failedRows.length > 0 && failedRowsRef.current) {
            failedRowsRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }, [failedRows]);

    return (
        <div className="flex flex-1 flex-col overflow-hidden p-6 bg-gray-50 min-h-screen">
            <h1 className="text-3xl font-bold text-text-primary mb-6">{title}</h1>

            <div className="bg-white rounded-xl shadow-md p-6 mb-6">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-semibold text-text-primary mb-4 flex items-center gap-2">
                        <FileText size={24} className="text-gmail-blue" /> Upload File
                    </h2>
                    {target == "batchproducts" && (
                        <Button 
                            variant="secondary"
                            size="sm"
                            onClick={() => setIsGuideOpen(true)}
                            className="flex items-center gap-2"
                        >
                            <Info size={16} /> View CSV Formatting Guide
                        </Button>
                    )}
                    {target == "batchrawmaterials" && (
                        <Button variant="secondary"
                            size="sm"
                            onClick={() => setIsGuideOpen(true)}
                            className="flex items-center gap-2"
                        >
                            <Info size={16} />View CSV Formatting Guide
                        </Button>
                    )}
                    {target == "batchcategories" && (
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => setIsGuideOpen(true)}
                            className="flex items-center gap-2"
                        >
                            <Info size={16} /> View CSV Formatting Guide
                        </Button>
                    )}
                    {target == "customer-groups" && (
                        <Button 
                            variant="secondary"
                            size="sm"
                            onClick={() => setIsGuideOpen(true)}
                            className="flex items-center gap-2"
                        >
                            <Info size={16} />View CSV Formatting Guide
                        </Button>
                    )}
                    {target == "customers" && (
                        <Button 
                            variant="secondary"
                            size="sm"
                            onClick={() => setIsGuideOpen(true)}
                            className="flex items-center gap-2"
                        >
                            <Info size={16} />View CSV Formatting Guide
                        </Button>
                    )}
                    {target == "grouped-customers" && (
                        <Button 
                            variant="secondary"
                            size="sm"
                            onClick={() => setIsGuideOpen(true)}
                            className="flex items-center gap-2"
                        >
                            <Info size={16} />View CSV Formatting Guide
                        </Button>
                    )}
                </div>
                <Input
                    type="file"
                    accept=".csv,.xls,.xlsx"
                    onChange={handleFile}
                    className="block w-full text-sm text-gray-500
                    file:mr-4 file:py-2 file:px-4
                    file:rounded-full file:border-0
                    file:text-sm file:font-semibold
                    file:bg-gmail-blue file:text-white
                    hover:file:bg-blue-600 cursor-pointer"
                />
                {fileName && <p className="mt-3 text-sm text-gray-600">Selected file: <span className="font-medium">{fileName}</span></p>}
            </div>

            {columns.length > 0 && (
                <div className="rounded-xl shadow-md p-6 bg-white mb-6">
                    <h2 className="text-xl font-semibold text-text-primary mb-4 flex items-center gap-2">
                        <Table size={24} className="text-gmail-green" /> Column Mapping
                    </h2>
                    <p className="text-sm text-text-secondary mb-4">
                        Map columns from your uploaded file to the expected schema for {tag}.
                        Suggested mappings are pre-selected. Select 'Ignore' for columns you don't need.
                    </p>
                    <div className="overflow-x-auto custom-scrollbar">
                        <table className="min-w-full text-sm divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Uploaded Column</th>
                                    <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mapped To</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {columns.map((col) => (
                                    <tr key={col}>
                                        <td className="py-3 px-4 font-medium text-gray-700 whitespace-nowrap">{col}</td>
                                        <td className="py-3 px-4">
                                            <select
                                                className="border border-gray-300 rounded-md shadow-sm px-3 py-2 bg-white text-gray-700 focus:ring-gmail-blue focus:border-gmail-blue text-sm"
                                                value={mapping[col] || 'ignore'}
                                                onChange={(e) => handleMappingChange(col, e.target.value)}
                                            >
                                                <option value="ignore">Ignore</option>
                                                {expectedSchema.map((field) => (
                                                    <option key={field} value={field}>
                                                        {field}
                                                    </option>
                                                ))}
                                            </select>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {rawData.length > 0 && ( // Show preview only if raw data exists
                <div className="mt-auto bg-white p-6 rounded-xl shadow-md flex-grow flex flex-col">
                    <div className="flex items-center justify-between py-2 mb-4">
                        <h2 className="text-xl font-semibold text-text-primary flex items-center gap-2">
                            <Info size={24} className="text-gmail-yellow" /> Transformed Preview
                        </h2>
                        <Button
                            type="button"
                            onClick={handleUpload}
                            variant="primary"
                            className="flex items-center justify-center py-2 px-4 text-md font-semibold"
                            disabled={loading || !user || !endpoint || transformedData.length === 0}
                        >
                            {loading ? (
                                <Loader2 className="animate-spin h-5 w-5 mr-2" />
                            ) : (
                                <Upload size={20} className="mr-2" />
                            )}
                            {loading ? 'Uploading...' : 'Upload Data'}
                        </Button>
                    </div>
                    <div className="flex-grow overflow-auto custom-scrollbar rounded-md border border-gray-200 bg-gray-50 p-4">
                        <pre className="whitespace-pre-wrap text-xs">
                            {JSON.stringify(pageData, null, 2)}
                        </pre>
                    </div>
                    <div className="flex justify-between items-center mt-4">
                        <Button
                            variant="secondary"
                            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                            className="px-4 py-2"
                        >
                            ⬅ Prev
                        </Button>
                        <span className="text-sm text-gray-600">Page {currentPage} of {Math.ceil(transformedData.length / rowsPerPage)}</span>
                        <Button
                            variant="secondary"
                            onClick={() => setCurrentPage((p) =>
                                p * rowsPerPage < transformedData.length ? p + 1 : p
                            )}
                            disabled={currentPage * rowsPerPage >= transformedData.length}
                            className="px-4 py-2"
                        >
                            Next ➡
                        </Button>
                    </div>
                </div>
            )}

            {failedRows.length > 0 && (
                <div ref={failedRowsRef} className="mt-8 bg-red-50 p-6 rounded-xl shadow border border-red-300">
                    <h2 className="text-xl font-semibold text-red-700 mb-2 flex items-center gap-2">
                        <XCircle size={24} className="text-red-600" /> Failed Rows
                    </h2>
                    <p className="text-sm text-red-600 mb-4">
                        The following rows failed to import. Please review and correct them.
                    </p>
                    <div className="overflow-auto custom-scrollbar rounded-md border border-red-200 bg-red-100 p-4">
                        <pre className="whitespace-pre-wrap text-xs">
                            {JSON.stringify(failedRows, null, 2)}
                        </pre>
                    </div>
                </div>
            )}
            <Modal
                isOpen={isGuideOpen}
                onClose={() => setIsGuideOpen(false)}
                title={guideData?.title || "CSV Formatting Guide"}
                size="wide"
            >
                {guideData ? (
                    <div className="space-y-6">
                        <div className="flex gap-4 text-sm font-medium text-gray-500 border-b pb-2">
                            <span>Format: <b className="text-gray-700">{guideData.format}</b></span>
                            <span>Delimiter: <b className="text-gray-700">"{guideData.delimiter}"</b></span>
                        </div>

                        {/* Using pre-wrap to respect the \n from your FastAPI string */}
                        <pre className="whitespace-pre-wrap font-sans text-sm text-gray-700 leading-relaxed bg-gray-50 p-4 rounded-md border">
                            {guideData.instructions}
                        </pre>

                        {guideData.uom_reference && (
                            <div>
                                <h4 className="font-bold text-sm mb-2 text-gray-800">System Preset Unit Codes (unitofmeasurement):</h4>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                    {guideData.uom_reference.map(uom => (
                                        <div key={uom.code} className="text-xs p-2 border rounded bg-gray-50">
                                            <span className="font-bold text-gmail-blue">{uom.code}</span>: {uom.name}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {guideData.currency_reference && (
                            <div>
                                <h4 className="font-bold text-sm mb-2 text-gray-800">System Preset Currency Codes (currency):</h4>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                    {guideData.currency_reference.map(curr => (
                                        <div key={curr.code} className="text-xs p-2 border rounded bg-gray-50">
                                            <span className="font-bold text-gmail-blue">{curr.code}</span>: {curr.name}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {guideData.groups_reference && (
                            <div>
                                <h4 className="font-bold text-sm mb-2 text-gray-800">Currently existing member groups:</h4>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                    {guideData.groups_reference.map(group => (
                                        <div key={group.id} className="text-xs p-2 border rounded bg-gray-50">
                                            <span className="font-bold text-gmail-blue">{group.id}</span>: {group.name}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}


                        {guideData.branches_reference && (
                            <div>
                                <h4 className="font-bold text-sm mb-2 text-gray-800">Branches of your organization:</h4>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                    {guideData.branches_reference.map(branch => (
                                        <div key={branch.id} className="text-xs p-2 border rounded bg-gray-50">
                                            <span className="font-bold text-gmail-blue">{branch.id}</span>: {branch.name}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="mt-4 flex justify-end">
                            <Button variant="primary" onClick={() => setIsGuideOpen(false)}>
                                Got it
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div className="flex justify-center p-10">
                        <Loader2 className="animate-spin text-gmail-blue" />
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default ImportData;
